import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import './App.css';
import { usePageSeo } from './usePageSeo';
import Navbar from './Components/Navbar';
import Footer from './Components/Footer';
import IntroLoader from './Components/IntroLoader';
import Home from './Components/Home';
const About = lazy(() => import('./Components/About'));
const Projects = lazy(() => import('./Components/Projects'));
const Company = lazy(() => import('./Components/Company'));
const Contact = lazy(() => import('./Components/Contact'));
const Cursor = lazy(() => import('./Components/Cursor'));

function DeferredCursor() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;

    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!finePointer.matches || reducedMotion.matches) return undefined;

    const enable = () => setReady(true);
    window.addEventListener('pointermove', enable, { once: true, passive: true });
    return () => window.removeEventListener('pointermove', enable);
  }, []);

  return ready ? <Suspense fallback={null}><Cursor /></Suspense> : null;
}

function RouteFallback() {
  return <div className="route-loading" role="status" aria-label="Loading page" />;
}

function DeferredPage({ kind, Component }) {
  return (
    <Page kind={kind}>
      <Suspense fallback={<RouteFallback />}>
        <Component />
      </Suspense>
    </Page>
  );
}

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [pathname]);
  return null;
}

function Page({ kind, children }) {
  usePageSeo(kind);
  return <main id="main-content" className={`page page--${kind}`}>{children}</main>;
}

function RouterView() {
  const location = useLocation();
  const firstRender = useRef(true);
  const routeLabel = location.pathname === '/' ? 'Home' : location.pathname.slice(1).replace(/(^|-)\w/g, (char) => char.toUpperCase());

  useEffect(() => {
    firstRender.current = false;
  }, []);

  return (
    <>
      <a className="skip-link" href="#main-content">Skip to content</a>
      <DeferredCursor />
      <IntroLoader />
      <Navbar />
      <ScrollManager />
      <div className="route-announcer" aria-live="polite" aria-atomic="true">{routeLabel} page</div>
      <div key={location.pathname} className={firstRender.current ? 'route-transition' : 'route-transition route-transition--enter'}>
        <Routes location={location}>
          <Route path="/" element={<Page kind="home"><Home /></Page>} />
          <Route path="/about" element={<DeferredPage kind="about" Component={About} />} />
          <Route path="/work" element={<DeferredPage kind="work" Component={Projects} />} />
          <Route path="/company" element={<DeferredPage kind="company" Component={Company} />} />
          <Route path="/contact" element={<DeferredPage kind="contact" Component={Contact} />} />
          <Route path="*" element={<Navigate replace to="/" />} />
        </Routes>
      </div>
      <Footer />
    </>
  );
}

function App() {
  const basename = process.env.PUBLIC_URL && process.env.PUBLIC_URL !== '.' ? process.env.PUBLIC_URL : '/';
  return (
    <BrowserRouter basename={basename}>
      <RouterView />
    </BrowserRouter>
  );
}

export default App;
